import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, PencilSimple, Trash } from "@phosphor-icons/react";
import { Mascot } from "@/components/styleguide/Mascot";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomInput } from "@/components/styleguide/ui/BloomInput";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { ChildAvatar } from "@/experiences/select-child/ChildAvatar";
import { cn } from "@/lib/utils";
import { api } from "./api";
import { VocabHeader } from "./VocabHeader";
import { setMode } from "./ParentGate";
import TrialEndedModal from "./TrialEndedModal";

const PREP_LABELS = { general: "General Vocabulary", grammar: "Grammar School", "11plus": "11+" };
const PREP_VARIANTS = { general: "teal", grammar: "blue", "11plus": "primary" };

function ChildDialog({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState({ first_name: "", school_year: 3, prep_type: "general", avatar: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initial || { first_name: "", school_year: 3, prep_type: "general", avatar: 0 });
      setError("");
    }
  }, [open, initial]);

  const save = async () => {
    if (!form.first_name.trim()) { setError("Please enter a name"); return; }
    setSaving(true);
    try {
      if (initial?.child_id) {
        await api(`/children/${initial.child_id}`, { method: "PATCH", body: form });
      } else {
        await api("/children", { method: "POST", body: form });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api(`/children/${initial.child_id}`, { method: "DELETE" });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <button aria-label="Close" onClick={onClose} className="absolute inset-0 bg-foreground/25 backdrop-blur-[2px]" />
          <motion.div
            data-testid="child-dialog"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            className="relative max-h-[90svh] w-full max-w-md overflow-y-auto rounded-t-[2rem] bg-card p-6 shadow-soft-lg sm:rounded-[2rem]"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-xl font-black text-foreground">{initial?.child_id ? "Edit child" : "Add a child"}</h2>
              <button onClick={onClose} aria-label="Close" className="flex h-11 w-11 items-center justify-center rounded-full bg-muted transition hover:bg-border">
                <X size={18} weight="bold" />
              </button>
            </div>

            <BloomInput label="First name" value={form.first_name} data-testid="child-name-input" onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="e.g. Maya" />

            <p className="mb-2 mt-4 text-sm font-bold text-foreground">School year</p>
            <div className="flex gap-2">
              {[3, 4, 5, 6].map((y) => (
                <button key={y} data-testid={`year-chip-${y}`} onClick={() => setForm({ ...form, school_year: y })}
                  className={cn("flex-1 rounded-2xl border-2 py-2.5 font-display font-extrabold transition", form.school_year === y ? "border-primary bg-accent text-primary-strong" : "border-border text-muted-foreground hover:border-primary/40")}>
                  Year {y}
                </button>
              ))}
            </div>

            <p className="mb-2 mt-4 text-sm font-bold text-foreground">Preparation</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PREP_LABELS).map(([k, label]) => (
                <button key={k} data-testid={`prep-chip-${k}`} onClick={() => setForm({ ...form, prep_type: k })}
                  className={cn("rounded-full border-2 px-4 py-2 text-sm font-extrabold transition", form.prep_type === k ? "border-primary bg-accent text-primary-strong" : "border-border text-muted-foreground hover:border-primary/40")}>
                  {label}
                </button>
              ))}
            </div>

            <p className="mb-2 mt-4 text-sm font-bold text-foreground">Avatar</p>
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2, 3, 4, 5].map((v) => (
                <button key={v} data-testid={`avatar-pick-${v}`} onClick={() => setForm({ ...form, avatar: v })}
                  className={cn("rounded-full border-2 p-0.5 transition", form.avatar === v ? "border-primary" : "border-transparent hover:border-border")}>
                  <ChildAvatar variant={v} className="h-12 w-12" />
                </button>
              ))}
            </div>

            {error && <p className="mt-3 text-sm font-bold text-destructive" data-testid="child-dialog-error">{error}</p>}

            <div className="mt-6 flex items-center gap-2">
              <BloomButton fullWidth onClick={save} disabled={saving} data-testid="child-save-btn">
                {saving ? "Saving…" : initial?.child_id ? "Save changes" : "Add child"}
              </BloomButton>
              {initial?.child_id && (
                <BloomButton variant="ghost" onClick={remove} disabled={saving} aria-label="Remove child" data-testid="child-remove-btn" className="text-destructive">
                  <Trash size={20} weight="bold" />
                </BloomButton>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function ChildrenPage() {
  const navigate = useNavigate();
  const [children, setChildren] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [paywallOpen, setPaywallOpen] = useState(false);

  const load = useCallback(async () => {
    setChildren(await api("/children"));
  }, []);

  useEffect(() => { load().catch(() => setChildren([])); }, [load]);

  const active = (children || []).filter((c) => c.active);

  const enter = (c) => {
    setMode(`child:${c.child_id}`);
    navigate(`/vocab/child/${c.child_id}`);
  };

  return (
    <div className="min-h-[100svh] bg-background">
      <VocabHeader onSubscribe={() => setPaywallOpen(true)} />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="font-display text-3xl font-black tracking-tight text-foreground sm:text-4xl">Your explorers</h1>
            <p className="mt-1 text-sm font-semibold text-muted-foreground">Tap a child to enter their Bloom. Grown-ups return with your PIN.</p>
          </div>
          <Mascot size={56} animated={false} mood="encouraging" className="hidden sm:block" />
        </div>

        <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {children === null && [1, 2, 3].map((i) => <div key={i} className="h-44 animate-pulse rounded-3xl bg-muted" />)}

          {active.map((c, i) => (
            <motion.div key={c.child_id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              onClick={() => enter(c)}
              data-testid={`enter-child-${c.child_id}`}
              className="relative cursor-pointer rounded-3xl border border-border bg-card p-5 shadow-soft transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-soft-lg active:scale-[0.98]">
              {/* Edit button — always visible, stops card click */}
              <button
                data-testid={`edit-child-${c.child_id}`}
                onClick={(e) => { e.stopPropagation(); setDialog({ open: true, initial: c }); }}
                aria-label={`Edit ${c.first_name}`}
                className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted/70 text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <PencilSimple size={17} weight="bold" />
              </button>
              <div className="flex items-center gap-4">
                <ChildAvatar variant={c.avatar} className="h-16 w-16 drop-shadow-sm" />
                <div>
                  <p className="font-display text-xl font-black text-foreground">{c.first_name}</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <BloomBadge variant="neutral" className="px-2.5 py-1 text-xs">Year {c.school_year}</BloomBadge>
                    <BloomBadge variant={PREP_VARIANTS[c.prep_type]} className="px-2.5 py-1 text-xs">{PREP_LABELS[c.prep_type]}</BloomBadge>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-center text-xs font-extrabold text-primary-strong">
                Tap to enter {c.first_name}'s Bloom →
              </p>
            </motion.div>
          ))}

          {children !== null && active.length < 2 && (
            <button
              data-testid="add-child-card"
              onClick={() => setDialog({ open: true, initial: null })}
              className="flex min-h-[176px] flex-col items-center justify-center gap-2.5 rounded-3xl border-2 border-dashed border-border bg-card/50 text-muted-foreground transition hover:border-primary/50 hover:text-primary-strong"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted"><Plus size={24} weight="bold" /></span>
              <span className="font-display font-extrabold">Add Child</span>
            </button>
          )}

          {children !== null && active.length >= 2 && (
            <div
              data-testid="add-child-limit"
              className="flex min-h-[176px] flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-border bg-muted/30 px-4 text-center text-muted-foreground"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Plus size={24} weight="bold" className="opacity-30" />
              </span>
              <p className="font-display text-sm font-extrabold">Maximum 2 children</p>
              <p className="text-xs font-semibold leading-snug">
                Your account supports up to 2 children.
              </p>
            </div>
          )}
        </div>

        {children !== null && active.length === 0 && (
          <div className="mt-8 flex flex-col items-center gap-2 text-center">
            <p className="text-sm font-semibold text-muted-foreground">No children yet — add your first explorer to begin!</p>
          </div>
        )}
      </main>

      <ChildDialog open={dialog.open} initial={dialog.initial} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />
      {paywallOpen && (
        <TrialEndedModal canDismiss onDismiss={() => setPaywallOpen(false)} />
      )}
    </div>
  );
}